import OrderAmountArea from "components/front/business/order/OrderAmountArea";
import SsHeadline from "components/front/layout/SsHeadline";
import OrderCourseOptionsArea from "components/front/mypage/order/OrderCourseOptionsArea";
import { ReceiptArea } from "components/front/mypage/order/ReceiptArea";
import OrderCourseArea from "components/front/order/OrderCourseArea";
import { isNotSupportEnded } from "lib/project_util";
import { groupBy, uniq } from "lodash";
import dynamic from "next/dynamic";
import React, { FC, useEffect, useMemo, useState } from "react";
import { Props as OrderAddressSectionProps } from "components/front/mypage/order/OrderAddressSection";
import { Props as OrderResidenceSectionProps } from "components/front/mypage/order/OrderResidenceSection";
import { Props as OrderCorporateAddressSectionProps } from "components/front/mypage/order/OrderCorporateAddressSection";
import { OrderPaymentArea } from "./OrderPaymentArea";
import { OrderDeliveryMethodArea } from "./OrderDeliveryMethodArea";
import StatusChip from "components/front/mypage/order/StatusChip";
import { SupportCommentArea } from "./SupportCommentArea";
import styled, { css } from "styled-components";
import { Course, Order, OrderAddress, OrderCorporateAddress, OrderDetail, OrderResidence } from "interfaces/front/page/mypage/supportPanel";
import useServiceType from "lib/hooks/front/useServiceType";
import QRCode from "qrcode.react";

const OrderResidenceSection = dynamic<OrderResidenceSectionProps>(() => import("components/front/mypage/order/OrderResidenceSection"));
const OrderAddressSection = dynamic<OrderAddressSectionProps>(() => import("components/front/mypage/order/OrderAddressSection"));
const OrderCorporateAddressSection = dynamic<OrderCorporateAddressSectionProps>(
  () => import("components/front/mypage/order/OrderCorporateAddressSection")
);

type Props = { order: Order };
const OrderArea: FC<Props> = ({ order }) => {
  const { orderDetailGroups, canChangeOption, showsPayment, courseIds, orderRecipient, isShopping, isEventSales, pickupCode } = useOrderAreaStates(order);
  const orderTypeLabel = isShopping ? "購入" : "応援";
  const [origin, setOrigin] = useState<string>("");
  useEffect(() => {
    if (typeof window !== "undefined") {
      setOrigin(window.location.origin);
    }
  }, []);
  const pickupQrValue = useMemo(() => {
    if (!pickupCode) return "";
    const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
    const normalized = base ? base.replace(/\/$/, "") : "";
    return `${normalized}/admin/backfront/${pickupCode}`;
  }, [origin, pickupCode]);
  return (
    <div key={order.code}>
      <StatusChip status={order.status} orderEventSaleStatus={order.order_event_sale?.status}  />
      <StyledSection>
        <div>
          <SsHeadline color="black" label={`${orderTypeLabel}内容を確認`} />
        </div>
        <OrderMeta>
          <OrderMetaText>{orderTypeLabel}ID: {order.code}</OrderMetaText>
          <OrderMetaText>{orderTypeLabel}日時: {new Date(order.ordered_at).toLocaleString()}</OrderMetaText>
        </OrderMeta>
        {orderDetailGroups.map((orderDetailGroup, index) => (
          <OrderItemArea key={index}>
            <OrderCourseArea course={orderDetailGroup.course} quantity={orderDetailGroup.orderDetails.length} isShopping={isShopping} />
            {orderDetailGroup.orderDetails[0].order_course_options.length > 0 && (
              <>
                <hr />
                <OrderCourseOptionsArea
                  orderDetails={orderDetailGroup.orderDetails}
                  canChangeOption={canChangeOption}
                  orderCode={order.code}
                />
              </>
            )}
          </OrderItemArea>
        ))}
        <AmountArea>
          <OrderAmountArea order={order} isShopping={isShopping} />
        </AmountArea>
        <ReceiptArea order={order} />
      </StyledSection>
      {isShopping && isEventSales && pickupCode && (
        <StyledSection>
          <SsHeadline color="black" label="引き換えQR" />
          <QrWrapper>
            <QrCodeWrapper>
              <QRCode value={pickupQrValue} size={150} />
            </QrCodeWrapper>
            <QrDescription>窓口でスタッフにご提示ください。</QrDescription>
          </QrWrapper>
        </StyledSection>
      )}
      {showsPayment && (
        <StyledSection>
          <SsHeadline color="black" label="お支払い情報を確認" />
          <OrderPaymentArea payment={order.order_payment} />
        </StyledSection>
      )}
      {order.order_delivery_method !== null && order.project.delivery_options?.length !== 1 && (
        <StyledSection>
          <SsHeadline color="black" label="商品の受け取り方法を確認" />
          <OrderDeliveryMethodArea delivery_method={order.order_delivery_method} />
        </StyledSection>
      )}
      {orderRecipient?.type === "address" && (
        <StyledOrderAddressSection
          orderCode={order.code}
          orderAddress={orderRecipient as OrderAddress}
          canChange={orderRecipient.can_change}
          courseIds={courseIds}
        />
      )}
      {orderRecipient?.type === "residence" && (
        <StyledOrderResidenceSection
          orderCode={order.code}
          orderRecipient={orderRecipient as OrderResidence}
          onestopFlag={order.order_project_requisite?.onestop_flag}
          canChange={orderRecipient.can_change}
          courseIds={courseIds}
        />
      )}
      {orderRecipient?.type === "corporate_address" && (
        <OrderCorporateAddressSection
          orderCode={order.code}
          recipient={orderRecipient as OrderCorporateAddress}
          canChange={orderRecipient.can_change}
          courseIds={courseIds}
        />
      )}
      {order.support_comment !== null && !isShopping && (
        <StyledSection>
          <SsHeadline color="black" label="応援コメントを確認" />
          <SupportCommentArea project={order.project} orderCode={order.code} supportComment={order.support_comment} />
        </StyledSection>
      )}
    </div>
  );
};
export default OrderArea;

const useOrderAreaStates = (order: Order) => {
  const canChangeOption = useMemo(() => isNotSupportEnded(order.project), [order.project]);
  const showsPayment = useMemo(() => order.order_payment.payment_method !== "no_method", [order.order_payment.payment_method]);
  const orderRecipient = order.order_recipient;
  const orderDetailGroups = useMemo<OrderDetailGroup[]>(() => {
    const orderDetails = order.order_details as OrderDetail[];
    const groupByCourse: Record<number, OrderDetail[]> = groupBy(orderDetails, (orderDetail) => orderDetail.course_id);
    return Object.entries(groupByCourse).map(([, orderDetails]) => ({ course: orderDetails[0].course, orderDetails: orderDetails }));
  }, [order]);
  const courseIds = useMemo(() => uniq(order.order_details.map((orderDetail) => Number(orderDetail.course_id))), [order]);
  const { isShopping } = useServiceType(order.project.service_type);
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 960) {
      window.scrollTo(0, 200);
    }
  }, [order]);
  const isEventSales = !!order.project.event_sales_flag;
  const pickupCode = order.order_event_sale?.pickup_code ?? null;
  return { orderDetailGroups, canChangeOption, showsPayment, courseIds, orderRecipient, isShopping, isEventSales, pickupCode };
};

const sectionCss = css`
  width: inherit;
  margin: 10px 0 30px;
`;
const StyledOrderAddressSection = styled(OrderAddressSection)`
  ${sectionCss};
`;
const StyledOrderResidenceSection = styled(OrderResidenceSection)`
  ${sectionCss};
`;
const StyledSection = styled.section`
  ${sectionCss};
`;
const OrderMeta = styled.div`
  margin: 0 0 10px;
`;
const OrderMetaText = styled.div`
  margin-bottom: 5px;
  color: #666;
  @media screen and (max-width: 960px) {
    font-size: 12px;
  }
  @media screen and (min-width: 961px) and (max-width: 1280px) {
    font-size: 13px;
  }
  @media screen and (min-width: 1281px) {
    font-size: 14px;
  }
`;
const OrderItemArea = styled.div`
  border: 1px solid #ccc;
  margin: 10px 0;
  @media screen and (max-width: 960px) {
    padding: 0 10px 10px;
  }
  @media screen and (min-width: 961px) {
    padding: 10px 20px 20px;
  }
`;
const AmountArea = styled.div`
  padding: 5px;
`;

const QrWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const QrCodeWrapper = styled.div`
  background-color: #ffffff;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
`;

const QrDescription = styled.div`
  font-size: 16px;
  text-align: center;
  line-height: 1.4;
`;

const QrPickupCode = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: center;
`;

const QrCodeText = styled.div`
  font-size: 14px;
  word-break: break-all;
  color: #555;
  text-align: center;
`;

type OrderDetailGroup = {
  course: Course;
  orderDetails: OrderDetail[];
};
